/**
 * Fetches iTunes enrichment fields (description, screenshots, release notes,
 * genres, etc.) in parallel with the core slice.
 */

import { useHydratedSearchParams } from '@/hooks/use-hydrated-search-params';
import { useQuery } from '@tanstack/react-query';
import { normalizeAppData, type NormalizedAppData } from '@/lib/app-data-helpers';

export function useAppItunes(appId: number) {
  const searchParams = useHydratedSearchParams();
  const country = searchParams.get('country') || '';

  const { data, isLoading, error } = useQuery<Partial<NormalizedAppData>, Error>({
    queryKey: ['app-itunes', appId, country],
    queryFn: async () => {
      const qs = new URLSearchParams({ appId: String(appId) });
      if (country) qs.set('country', country);
      const res = await fetch(`/api/app-detail/itunes?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch app iTunes data');
      const payload = await res.json();
      return normalizeAppData(payload);
    },
    enabled: !!appId,
    staleTime: 30 * 60 * 1000,
  });

  return { data: data || null, isLoading, error: error?.message || null };
}
